import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Box, Dialog, IconButton, Link, Toolbar, Tooltip, Typography } from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { OrderProofAttachment } from "../../types/order";
import { downloadFromUrl } from "../../utils/downloadFromUrl";
import { getQaBannerOffsetPx } from "../../utils/environment";

type TranslationKeys = keyof typeof translations.en;

interface OrderProofAttachmentsProps {
  attachments: OrderProofAttachment[];
  layout?: "stacked" | "inline";
  initialAttachmentId?: string;
  onViewerClose?: () => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;
const INLINE_VISIBLE_LIMIT = 3;

const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

interface PanOffset {
  x: number;
  y: number;
}

const OrderProofAttachments: React.FC<OrderProofAttachmentsProps> = ({
  attachments,
  layout = "stacked",
  initialAttachmentId,
  onViewerClose,
}) => {
  const { t } = useTranslation();
  const [viewer, setViewer] = useState<OrderProofAttachment | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState<PanOffset>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const qaBannerOffset = getQaBannerOffsetPx();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const isInline = layout === "inline";

  const applyZoom = (nextZoom: number) => {
    const clampedZoom = clampZoom(nextZoom);
    setZoom(clampedZoom);
    if (clampedZoom === MIN_ZOOM) {
      setPan({ x: 0, y: 0 });
    }
  };

  const resetView = useCallback(() => {
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
  }, []);

  const closeViewer = () => {
    resetView();
    setViewer(null);
    onViewerClose?.();
  };

  const openViewer = (attachment: OrderProofAttachment) => {
    resetView();
    setViewer(attachment);
  };

  useEffect(() => {
    if (!initialAttachmentId) {
      return;
    }

    const attachment = attachments.find((item) => item.id === initialAttachmentId);
    if (attachment) {
      resetView();
      setViewer(attachment);
    }
  }, [attachments, initialAttachmentId, resetView]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !viewer) {
      return undefined;
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      setZoom((currentZoom) => {
        const nextZoom = clampZoom(currentZoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
        if (nextZoom === MIN_ZOOM) {
          setPan({ x: 0, y: 0 });
        }
        return nextZoom;
      });
    };

    viewport.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", handleWheel);
  }, [viewer]);

  const handleDownload = async (attachment: OrderProofAttachment) => {
    if (!attachment.url) {
      return;
    }
    setDownloadingId(attachment.id);
    try {
      await downloadFromUrl(attachment.url, attachment.filename);
    } catch {
      window.open(attachment.url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (zoom <= MIN_ZOOM) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    panStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setIsPanning(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!panStartRef.current) {
      return;
    }
    setPan({
      x: panStartRef.current.panX + (event.clientX - panStartRef.current.x),
      y: panStartRef.current.panY + (event.clientY - panStartRef.current.y),
    });
  };

  const endPan = (event: React.PointerEvent<HTMLDivElement>) => {
    if (panStartRef.current) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    panStartRef.current = null;
    setIsPanning(false);
  };

  if (attachments.length === 0) {
    return null;
  }

  const visibleAttachments = isInline ? attachments.slice(0, INLINE_VISIBLE_LIMIT) : attachments;
  const hiddenAttachments = isInline ? attachments.slice(INLINE_VISIBLE_LIMIT) : [];

  const renderAttachmentLink = (attachment: OrderProofAttachment) => {
    const link = (
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={() => openViewer(attachment)}
        data-testid={TestId.ORDER.PROOF_OPEN(attachment.id)}
        sx={{
          fontWeight: 600,
          fontSize: "body2.fontSize",
          textAlign: "left",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...(isInline && { maxWidth: 140 }),
        }}
      >
        {attachment.filename}
      </Link>
    );

    if (isInline) {
      return (
        <Tooltip key={attachment.id} title={attachment.filename}>
          <span>{link}</span>
        </Tooltip>
      );
    }

    return link;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: isInline ? "row" : "column",
          flexWrap: isInline ? "wrap" : "nowrap",
          alignItems: isInline ? "center" : "stretch",
          columnGap: 0.75,
          rowGap: 0.5,
          minWidth: 0,
        }}
      >
        {visibleAttachments.map((attachment, index) => (
          <Box
            key={attachment.id}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            {index > 0 && isInline ? (
              <Box component="span" sx={{ color: "text.disabled" }}>
                ·
              </Box>
            ) : null}
            {renderAttachmentLink(attachment)}
            {!isInline ? (
              <Tooltip title={t("orders.proofDownload" as TranslationKeys)}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(attachment)}
                    disabled={!attachment.url || downloadingId === attachment.id}
                    aria-label={t("orders.proofDownload" as TranslationKeys)}
                    data-testid={TestId.ORDER.PROOF_DOWNLOAD(attachment.id)}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        ))}
        {hiddenAttachments.length > 0 ? (
          <Tooltip title={hiddenAttachments.map((attachment) => attachment.filename).join(", ")}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {`+${hiddenAttachments.length}`}
            </Typography>
          </Tooltip>
        ) : null}
      </Box>

      <Dialog
        fullScreen
        open={Boolean(viewer)}
        onClose={closeViewer}
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              flexDirection: "column",
              ...(qaBannerOffset > 0 && {
                top: qaBannerOffset,
                height: `calc(100% - ${qaBannerOffset}px)`,
                maxHeight: `calc(100% - ${qaBannerOffset}px)`,
              }),
            },
          },
        }}
      >
        <Box
          data-testid={TestId.ORDER.PROOF_VIEWER}
          sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}
        >
          <Toolbar
            sx={{
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              gap: 1,
            }}
          >
            <IconButton
              edge="start"
              onClick={closeViewer}
              aria-label={t("Close" as TranslationKeys)}
              data-testid={TestId.ORDER.PROOF_VIEWER_CLOSE}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ flex: 1 }} noWrap>
              {viewer?.filename}
            </Typography>
            <Tooltip title={t("orders.proofZoomOut" as TranslationKeys)}>
              <span>
                <IconButton
                  onClick={() => applyZoom(zoom - ZOOM_STEP)}
                  disabled={zoom <= MIN_ZOOM}
                  aria-label={t("orders.proofZoomOut" as TranslationKeys)}
                  data-testid={TestId.ORDER.PROOF_VIEWER_ZOOM_OUT}
                >
                  <ZoomOutIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("orders.proofZoomIn" as TranslationKeys)}>
              <span>
                <IconButton
                  onClick={() => applyZoom(zoom + ZOOM_STEP)}
                  disabled={zoom >= MAX_ZOOM}
                  aria-label={t("orders.proofZoomIn" as TranslationKeys)}
                  data-testid={TestId.ORDER.PROOF_VIEWER_ZOOM_IN}
                >
                  <ZoomInIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("orders.proofZoomReset" as TranslationKeys)}>
              <span>
                <IconButton
                  onClick={resetView}
                  disabled={zoom === MIN_ZOOM && pan.x === 0 && pan.y === 0}
                  aria-label={t("orders.proofZoomReset" as TranslationKeys)}
                  data-testid={TestId.ORDER.PROOF_VIEWER_ZOOM_RESET}
                >
                  <RestartAltIcon />
                </IconButton>
              </span>
            </Tooltip>
            {viewer && (
              <IconButton
                onClick={() => handleDownload(viewer)}
                disabled={!viewer.url || downloadingId === viewer.id}
                aria-label={t("orders.proofDownload" as TranslationKeys)}
                data-testid={TestId.ORDER.PROOF_VIEWER_DOWNLOAD}
              >
                <DownloadIcon />
              </IconButton>
            )}
          </Toolbar>
          <Box
            ref={viewportRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endPan}
            onPointerCancel={endPan}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "common.black",
              p: 2,
              overflow: "hidden",
              touchAction: "none",
              cursor: zoom > MIN_ZOOM ? (isPanning ? "grabbing" : "grab") : "default",
            }}
          >
            {viewer?.url ? (
              <Box
                component="img"
                src={viewer.url}
                alt={viewer.filename}
                data-testid={TestId.ORDER.PROOF_VIEWER_IMAGE}
                draggable={false}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: "center center",
                  transition: isPanning ? "none" : "transform 0.15s ease",
                  userSelect: "none",
                }}
              />
            ) : null}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default OrderProofAttachments;
